import * as React from 'react';
import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import { Button } from 'Src/components/Button/Button';
import { AppSchoolsUser2 } from '../../../AppSchools2';
import { SchoolImageWithTicket } from 'Src/models/schoolImages';
import * as propz from 'propz';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import {
  getSchoolUserAllSchoolJobImages,
  getSchoolUserSchoolJobImage,
  updateSchoolUserSchoolJobImage,
  createSchoolUserSchoolJobImageTicket,
  createSchoolUserImagePreview,
} from 'Src/services/schoolUser/schoolJobImages';
import { approveSchoolJob } from 'Src/services/schoolUser/schoolJobs';
import { getSchoolUserSchoolUser } from 'Src/services/schoolUser/schools';
import { uploadFileSchoolUser } from 'Src/services/file';
import { FONT_NAME } from 'Src/consts/images';
import SortableItem from 'Src/components/SortableItem/SortableItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { HTMLEditorQuill } from 'Src/components/HTMLEditorQuill/HTMLEditorQuill';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { SCHOOL_JOB_STATUS } from 'Src/consts/schoolJob';

const buildVisibleRows = (students: GroupStudent[]): { student: GroupStudent; globalIndex: number }[][] => {
  const rows: { student: GroupStudent; globalIndex: number }[][] = [];
  let group: { student: GroupStudent; globalIndex: number }[] = [];
  students.forEach((s, idx) => {
    if (s.name.trim().toLowerCase() === 'new row') {
      if (group.length > 0) {
        rows.push([...group]);
      }
      group = [];
    } else {
      group.push({ student: s, globalIndex: idx });
    }
  });
  if (group.length > 0) rows.push(group);
  return rows;
};

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result.map((s, idx) => ({ ...s, order: idx + 1 }));
}

const MarkerItem: React.FC<{
  student: GroupStudent;
  index: number;
  onDragStart: (index: number) => void;
  onDrop: (index: number) => void;
}> = ({ student, index, onDragStart, onDrop }) => {
  return (
    <div
      draggable
      onDragStart={e => {
        e.dataTransfer.setData('text/plain', index.toString());
        onDragStart(index);
      }}
      onDragOver={e => e.preventDefault()}
      onDrop={e => {
        const dragIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
        onDrop(dragIdx);
      }}
      style={{
        width: '100%',
        textAlign: 'center',
        padding: '4px 0',
        borderTop: '1px dashed #ccc',
        margin: '8px 0',
        color: '#777',
        fontStyle: 'italic',
        display: 'none',
      }}
    >
      Row Break
    </div>
  );
};

export interface GroupStudent {
  _id?: string;
  name: string;
  surname: string;
  order?: number;
}

interface GroupPhotoFormValues {
  groupName: string;
  fontName: string;
  groupStudents: GroupStudent[];
  crestLeft: string;
  crestRight: string;
  commentContent: string;
}

interface Props {
  user: AppSchoolsUser2;
  image?: SchoolImageWithTicket;
  onCancel?: () => void;
  onSubmit?: (data: GroupPhotoFormValues) => void;
  readOnly?: boolean;
}

const GroupPhotoSchema = Yup.object().shape({
  groupName: Yup.string().required('Group name is required'),
  fontName: Yup.string().required('Font selection is required'),
  groupStudents: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      surname: Yup.string().test('nameOrSurname', 'Either name or surname must be provided', function(value) {
        const { name } = this.parent;
        return !!((name && name.trim()) || (value && value.trim()));
      }),
    })
  ),
  commentContent: Yup.string(),
});

const fontMapping: Record<string, string> = {
  ARIAL: 'Arial',
  GILL_SANS: 'Gill Sans',
  HELVETICA: 'Helvetica',
  TIMES_NEW_ROMAN: 'Times New Roman',
};

const crestContainerStyle = {
  width: '150px',
  height: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #ccc',
  backgroundColor: '#f8f9fa',
};

export function GroupPhotoEdit(props: Props) {
  const { user, image, onCancel, onSubmit: propOnSubmit, readOnly = false } = props;

  const location = useLocation();

  const history = useHistory();
  const { schoolJobId, schoolImageId } = useParams() as { schoolJobId: string; schoolImageId: string };

  const initialImage: SchoolImageWithTicket | undefined = props.image || (location.state && location.state.image);

  const [currentImage, setCurrentImage] = React.useState<SchoolImageWithTicket | undefined>(initialImage);
  const [saved, setSaved] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [formKey, setFormKey] = React.useState(currentImage?.id || '');
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [leftFileName, setLeftFileName] = React.useState('No file chosen');
  const [rightFileName, setRightFileName] = React.useState('No file chosen');
  const [showPreview, setShowPreview] = React.useState(false);
  const [previewId, setPreviewId] = React.useState<string>('');
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);

  const [isDetailEditing, setIsDetailEditing] = React.useState(true);
  const [isNameEditing, setIsNameEditing] = React.useState(false);

  const [isEdited, setIsEdited] = React.useState<boolean>(!!initialImage?.isEdited);
  const [editTicket, setEditTicket] = React.useState<{ ticket: string; generatedAt: number } | null>(null);

  const [approvalLoading, setApprovalLoading] = React.useState(false);

  const [commentAuthor, setCommentAuthor] = React.useState<any>(null);
  const [commentUpdater, setCommentUpdater] = React.useState<any>(null);

  const [isConfirmFinalApproveOpen, setIsConfirmFinalApproveOpen] = useState(false);
  const [localReadOnly, setLocalReadOnly] = useState(false);

  const isReadOnly = readOnly || Boolean(location.state?.readOnly) || localReadOnly;

  const commentMetaStyle: React.CSSProperties = {
    marginTop: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '0.875rem',
    color: '#6c757d',
  };

  React.useEffect(() => {
    if (schoolImageId && schoolJobId) {
      getSchoolUserSchoolJobImage(user, schoolJobId, schoolImageId)
        .then(fetchedImage => {
          if (fetchedImage) {
            setCurrentImage(fetchedImage);
            setIsEdited(!!fetchedImage.isEdited);

            setFormKey(fetchedImage.id);
          } else {
            console.error('No image returned for id:', schoolImageId);
          }
        })
        .catch(err => console.error('Error fetching image:', err));
    }
  }, [schoolImageId, schoolJobId, user]);

  const TICKET_VALIDITY_DURATION = 60 * 60 * 1000;
  function ticketIsExpired(ticket: any): boolean {
    if (!ticket || !ticket.generatedAt) return true;
    return Date.now() >= ticket.generatedAt + TICKET_VALIDITY_DURATION;
  }

  React.useEffect(() => {
    if (currentImage && currentImage.key && currentImage.mimeType) {
      if (!currentImage.ticket || ticketIsExpired(currentImage.ticket)) {
        createSchoolUserSchoolJobImageTicket(user, {
          key: currentImage.key,
          mimeType: currentImage.mimeType,
        })
          .then(ticket => {
            const extendedTicket = { ...ticket, generatedAt: Date.now() };
            setCurrentImage(prev => ({ ...prev, ticket: extendedTicket }));
          })
          .catch(err => console.error('Error generating main image ticket:', err));
      }
    }
  }, [currentImage, user]);

  React.useEffect(() => {
    if ((isDetailEditing || isEdited) && currentImage?.keyForEdit && currentImage?.mimeTypeForEdit) {
      const expired = !editTicket || Date.now() >= editTicket.generatedAt + TICKET_VALIDITY_DURATION;
      if (expired) {
        createSchoolUserSchoolJobImageTicket(user, {
          key: currentImage.keyForEdit,
          mimeType: currentImage.mimeTypeForEdit,
        })
          .then(t => setEditTicket({ ...t, generatedAt: Date.now() }))
          .catch(err => console.error('Error generating edit image ticket:', err));
      }
    }
  }, [isDetailEditing, isEdited, currentImage?.keyForEdit, currentImage?.mimeTypeForEdit, editTicket]);

  React.useEffect(() => {}, [previewId]);

  const getInitialValues = (): GroupPhotoFormValues => {
    if (!currentImage) {
      return {
        groupName: '',
        fontName: FONT_NAME.ARIAL,
        groupStudents: [],
        crestLeft: '',
        crestRight: '',
        commentContent: '',
      };
    }
    const sortedStudents = Array.isArray(currentImage.groupStudents)
      ? currentImage.groupStudents.slice().sort((a, b) => (a.order || 0) - (b.order || 0))
      : [];
    return {
      groupName: currentImage.groupName || '',
      fontName: currentImage.fontName ? currentImage.fontName.toUpperCase() : 'ARIAL',
      groupStudents: sortedStudents.map(student => ({
        _id: student._id,
        name: propz.get(student, ['name'], ''),
        surname: propz.get(student, ['surname'], ''),
        order: student.order,
      })),
      crestLeft: currentImage.crestLeft ? currentImage.crestLeft.pic : '',
      crestRight: currentImage.crestRight ? currentImage.crestRight.pic : '',
      commentContent: currentImage.comment?.content || '',
    };
  };

  React.useEffect(() => {
    if (currentImage?.comment?.createdBy) {
      getSchoolUserSchoolUser(user, currentImage.comment.createdBy)
        .then(u => {
          setCommentAuthor({
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
          });
        })
        .catch(() => setCommentAuthor({ firstName: 'Unknown', lastName: '', email: '' }));
    }

    if (currentImage?.comment?.updatedBy) {
      getSchoolUserSchoolUser(user, currentImage.comment.updatedBy)
        .then(u => {
          setCommentUpdater({
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
          });
        })
        .catch(() => setCommentUpdater({ firstName: 'Unknown', lastName: '', email: '' }));
    }
  }, [currentImage, user]);

  const isMounted = React.useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleFontChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const newFont = e.target.value;
    setFieldValue('fontName', newFont);
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (fieldName === 'crestLeft') {
        setLeftFileName(file.name);
      } else if (fieldName === 'crestRight') {
        setRightFileName(file.name);
      }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/bmp'];
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Only JPEG, PNG, and BMP files are allowed.');
        return;
      }
      const reader = new FileReader();
      reader.onload = event => {
        const img = new Image();
        img.onload = () => {
          if (img.width >= 200 && img.height >= 200) {
            setUploadError(null);
            const formData = new FormData();
            formData.append('file', file);
            uploadFileSchoolUser(user, formData)
              .then(data => {
                setFieldValue(fieldName, data.url);
              })
              .catch(err => {
                console.error('Error uploading file:', err);
              });
          } else {
            setUploadError('Selected image must be at least 200x200 pixels.');
          }
        };
        if (event.target?.result) {
          img.src = event.target.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePreview = () => {
    if (!currentImage) return;
    const previewParams = {
      imageId: currentImage.id,
      job: currentImage.job,
      fileName: currentImage.fileName,
      mimeType: currentImage.mimeType,
      key: currentImage.key,
      groupName: currentImage.groupName,
      school: currentImage.school,
      photoType: currentImage.photoType,
      fontName: currentImage.fontName,
      groupStudents: currentImage.groupStudents,
      crestLeft: currentImage.crestLeft ? currentImage.crestLeft.pic : '',
      crestRight: currentImage.crestRight ? currentImage.crestRight.pic : '',
    };
    createSchoolUserImagePreview(user, previewParams)
      .then(data => {
        setPreviewId(data.previewId);
        setShowPreview(true);
      })
      .catch(err => console.error('Error generating preview:', err));
  };

  const handleApprove = () => {
    if (!currentImage) return;
    const jobId = String(currentImage.job.jobId);

    getSchoolUserAllSchoolJobImages(user, jobId, {})
      .then(allImages => {
        const stillUnapproved = allImages.filter(img => !img.isApproved && img.id !== currentImage.id);

        if (stillUnapproved.length > 0) {
          approveSingleImage();
        } else {
          setIsConfirmFinalApproveOpen(true);
        }
      })
      .catch(err => console.error('Couldn’t fetch job images:', err));
  };

  function approveSingleImage() {
    if (!currentImage) return;
    setApprovalLoading(true);
    const jobId = String(currentImage.job.jobId);

    updateSchoolUserSchoolJobImage(user, jobId, currentImage.id, { isApproved: true })
      .then(updated => {
        setCurrentImage(img => img && { ...img, isApproved: true });
      })
      .catch(err => console.error('Error approving image', err))
      .finally(() => setApprovalLoading(false));
  }

  function finalApprove() {
    if (!currentImage) return;
    setApprovalLoading(true);
    const jobId = String(currentImage.job.jobId);

    updateSchoolUserSchoolJobImage(user, jobId, currentImage.id, { isApproved: true })
      .then(() => approveSchoolJob(user, jobId))
      .then(updatedJob => {
        setCurrentImage(
          img =>
            img && {
              ...img,
              isApproved: true,
              job: { ...img.job, status: SCHOOL_JOB_STATUS.APPROVED_BY_SCHOOL },
            }
        );
        setLocalReadOnly(true);
      })
      .catch(err => console.error('Error during final approve', err))
      .finally(() => {
        setApprovalLoading(false);
        setIsConfirmFinalApproveOpen(false);
      });
  }

  const handleCancelApproval = () => {
    if (!currentImage) return;
    setApprovalLoading(true);
    updateSchoolUserSchoolJobImage(user, String(currentImage.job.jobId), currentImage.id, { isApproved: false })
      .then(updated => {
        setCurrentImage(prev => (prev ? { ...prev, isApproved: false } : prev));
      })
      .catch(err => console.error('Error cancelling approval', err))
      .finally(() => setApprovalLoading(false));
  };

  const defaultOnSubmit = async (values: GroupPhotoFormValues, formikHelpers: FormikHelpers<GroupPhotoFormValues>) => {
    if (!currentImage) return;
    const cleanedStudents = [...values.groupStudents];
    while (
      cleanedStudents.length > 0 &&
      cleanedStudents[cleanedStudents.length - 1].name.trim().toLowerCase() === 'new row'
    ) {
      cleanedStudents.pop();
    }
    const updatedGroupStudents = cleanedStudents.map((student, index) => ({
      ...student,
      order: index + 1,
    }));
    const jobId = String(currentImage.job.jobId);
    const updateData: any = {
      groupName: values.groupName,
      fontName: values.fontName,
      groupStudents: updatedGroupStudents,
      isEdited: true,
      'comment.content': values.commentContent,
      'comment.updatedBy': user.userId,
      'comment.updatedAt': new Date(),
    };
    if (values.crestLeft) {
      updateData.crestLeft = { pic: values.crestLeft };
    }
    if (values.crestRight) {
      updateData.crestRight = { pic: values.crestRight };
    }
    if (!currentImage?.comment?.content) {
      updateData['comment.createdBy'] = user.userId;
      updateData['comment.createdAt'] = new Date();
    }
    updateSchoolUserSchoolJobImage(user, jobId, currentImage.id, updateData)
      .then(updatedImage => {
        const completeImage = {
          ...updatedImage,
          key: updatedImage.key || currentImage.key,
          mimeType: updatedImage.mimeType || currentImage.mimeType,
        };
        return createSchoolUserSchoolJobImageTicket(user, {
          key: completeImage.key,
          mimeType: completeImage.mimeType,
        }).then(ticket => ({ ...completeImage, ticket: { ...ticket, generatedAt: Date.now() } }));
      })
      .then(mergedImage => {
        if (isMounted.current) {
          mergedImage.isEdited = true;
          setIsEdited(true);
          setCurrentImage({
            ...mergedImage,
            groupStudents: updatedGroupStudents,
          });
          formikHelpers.resetForm({
            values: {
              groupName: mergedImage.groupName,
              fontName: mergedImage.fontName,
              groupStudents: updatedGroupStudents,
              crestLeft: mergedImage.crestLeft ? mergedImage.crestLeft.pic : '',
              crestRight: mergedImage.crestRight ? mergedImage.crestRight.pic : '',
              commentContent: mergedImage.comment?.content || '',
            },
          });
          setHasChanges(false);
          setSaved(true);
          setIsDetailEditing(true);
          setIsNameEditing(false);
          setTimeout(() => {
            if (isMounted.current) {
              setSaved(false);
              setFormKey(mergedImage.id + '-' + new Date().getTime());
            }
          }, 200);
        }
      })
      .catch(err => {
        console.error('Error updating group order and crest images', err);
      })
      .finally(() => {
        formikHelpers.setSubmitting(false);
      });
  };

  const onSubmit = props.onSubmit || defaultOnSubmit;

  if (!currentImage) {
    return <div>Error: No image provided.</div>;
  }

  return (
    <div className="container-fluid group-photo-edit-page">
      <Button onClick={() => history.goBack()} text="Back" customClass="mb-3 btn btn-secondary" />
      {saved && (
        <div className="alert alert-success" role="alert">
          Changes saved!
        </div>
      )}
      {uploadError && (
        <div className="alert alert-danger" role="alert">
          {uploadError}
        </div>
      )}
      {showPreview && (
        <div
          className="modal-overlay"
          onClick={() => setShowPreview(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '4px',
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
            }}
          >
            <button
              onClick={() => setShowPreview(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '40px',
                height: '40px',
                background: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
              }}
            >
              &times;
            </button>
            <p
              style={{
                fontStyle: 'italic',
                marginBottom: '10px',
                color: '#FF4500',
                textAlign: 'center',
                fontSize: '1.5em',
              }}
            >
              This content is for preview only. The final printed result may differ.
            </p>
            <img
              src={`${window.apiBase}/schoolUser/imagePreview/${previewId}?t=${Date.now()}`}
              alt="Preview"
              style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
      {isConfirmFinalApproveOpen && (
        <SimpleModal isOpen customClass="custom-user-modal">
          <div style={{ padding: 20 }}>
            <h2>Approve Last Image?</h2>
            <p>
              Once you approve this image, <strong>all</strong> images in this job will be approved and the job will
              move to “Approved by School.” If you need to do any more checks, cancel and review first.
            </p>
            <div className="d-flex justify-content-end" style={{ gap: 8, marginTop: 20 }}>
              <Button
                onClick={finalApprove}
                text="Yes, approve and finish job"
                customClass="modern-button modern-save-button"
              />
              <Button
                onClick={() => setIsConfirmFinalApproveOpen(false)}
                text="Cancel"
                customClass="modern-button modern-cancel-button"
              />
            </div>
          </div>
        </SimpleModal>
      )}
      <Formik
        key={formKey}
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={GroupPhotoSchema}
        onSubmit={isReadOnly ? () => {} : onSubmit}
      >
        {({ values, setFieldValue, resetForm, dirty }) => {
          const handleFullReorder = (dragIdx: number, dropIdx: number) => {
            const newOrder = reorder(values.groupStudents, dragIdx, dropIdx);
            setFieldValue('groupStudents', newOrder);
            setHasChanges(true);
          };

          const handleSplit = (globalIndex: number) => {
            const currentList = values.groupStudents;
            const newMarker: GroupStudent = { name: 'new row', surname: '', order: 0 };
            const newList = [
              ...currentList.slice(0, globalIndex + 1),
              newMarker,
              ...currentList.slice(globalIndex + 1),
            ];
            const recalculated = newList.map((s, idx) => ({ ...s, order: idx + 1 }));
            setFieldValue('groupStudents', recalculated);
            setHasChanges(true);
          };

          const visibleRows = buildVisibleRows(values.groupStudents);
          const inEditMode = isDetailEditing || isNameEditing || isEdited;

          const ticketToUse = inEditMode ? editTicket?.ticket : currentImage.ticket?.ticket;

          const [preloadedUrl, setPreloadedUrl] = React.useState<string | null>(null);

          const displayUrl = ticketToUse ? `${window.apiFile}/storage/images/${ticketToUse}` : null;

          React.useEffect(() => {
            if (!ticketToUse) return;
            const url = `${window.apiFile}/storage/images/${ticketToUse}`;
            const img = new Image();
            img.onload = () => setPreloadedUrl(url);
            img.src = url;
          }, [ticketToUse]);

          const isOriginal = !inEditMode;

          return (
            <Form>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>
                  {isReadOnly ? 'View Group Photo' : 'Edit Group Photo'}
                  {values.groupName ? `: ${values.groupName}` : ''}
                </h1>
                {(isEdited || isDetailEditing || isNameEditing) && (
                  <div className="d-flex align-items-center">
                    {!currentImage.isApproved && !isReadOnly && (
                      <Button
                        onClick={handleApprove}
                        text="Approve Image"
                        customClass="modern-button modern-save-button mr-2"
                        disabled={approvalLoading}
                      />
                    )}

                    {currentImage.isApproved && (
                      <>
                        <span className="text-success mr-3">
                          <FontAwesomeIcon icon={faCheckCircle} /> Approved
                        </span>
                        {!isReadOnly && (
                          <Button
                            onClick={handleCancelApproval}
                            text="Cancel Approval"
                            customClass="modern-button modern-cancel-button mr-2"
                            disabled={approvalLoading}
                          />
                        )}
                      </>
                    )}

                    <Button
                      onClick={handlePreview}
                      text="Preview"
                      customClass="modern-button modern-save-button"
                      disabled={hasChanges || dirty}
                    />
                  </div>
                )}
              </div>
              {(isDetailEditing || isNameEditing || isEdited) && (
                <div className="alert alert-info">
                  {isReadOnly
                    ? 'Here you can view the group name, font style, crest images, and student names as they were submitted. Click “Preview” to see the final caption layout.'
                    : 'Here you can update the group name, font style, crest images, and student names. Once you’ve saved your changes, click “Preview” to see how they look in the name caption area.'}
                </div>
              )}
              {(hasChanges || dirty) && (
                <div className="alert alert-warning" role="alert">
                  You have unsaved changes! Please scroll down and click Save to update your changes.
                </div>
              )}
              <div className="row">
                <div className={`col-12 ${isDetailEditing || isEdited ? 'col-md-8' : ''}`}>
                  <img
                    className="img-fluid img-thumbnail"
                    src={preloadedUrl || displayUrl || undefined}
                    alt={currentImage.fileNameForEdit || currentImage.fileName}
                    style={{
                      width: '100%',
                      maxHeight: isOriginal ? '600px' : '400px',
                      objectFit: 'contain',
                    }}
                  />
                  {!(preloadedUrl || displayUrl) && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: isOriginal ? 600 : 400, position: 'absolute', width: '100%' }}
                    >
                      <div className="spinner-border" role="status">
                        <span className="sr-only">Loading image…</span>
                      </div>
                    </div>
                  )}

                  {!isEdited && !isDetailEditing && !isNameEditing && (
                    <div className="mt-3 text-center">
                      <Button
                        onClick={() => {
                          setIsDetailEditing(true);
                          setIsNameEditing(false);
                        }}
                        text="Edit"
                        customClass="modern-button modern-edit-button"
                      />
                    </div>
                  )}

                  {isNameEditing ? (
                    <>
                      <div className="mt-3 text-center" style={{ fontWeight: 'bold' }}>
                        Drag and drop the student names to rearrange their order. Click the × to remove, or the Enter
                        icon to split.
                      </div>
                      <div
                        style={{
                          overflowX: 'auto',
                          padding: '10px',
                          border: '1px dashed #ccc',
                          marginTop: '8px',
                        }}
                      >
                        {visibleRows.map((row, rowIndex) => (
                          <div key={rowIndex} style={{ display: 'flex', gap: '10px', marginBottom: '5px' }}>
                            {row.map(({ student, globalIndex }) => (
                              <SortableItem
                                key={student._id || globalIndex}
                                index={globalIndex}
                                student={student}
                                values={values}
                                setFieldValue={setFieldValue}
                                setHasChanges={setHasChanges}
                                onDragStart={idx => setDragIndex(idx)}
                                onDrop={(to, from) => {
                                  if (from !== to) {
                                    handleFullReorder(from, to);
                                  }
                                }}
                                fontName={values.fontName}
                                onSplit={idx => handleSplit(idx)}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                      <div className="mt-2">
                        <Button
                          type="button"
                          onClick={() => {
                            const list = values.groupStudents;
                            const last = list[list.length - 1];
                            if (!last || last.name.trim().toLowerCase() !== 'new row') {
                              setFieldValue('groupStudents', [
                                ...list,
                                { name: 'new row', surname: '', order: 0 },
                                { name: '', surname: '', order: 0 },
                              ]);
                            } else {
                              setFieldValue('groupStudents', [...list, { name: '', surname: '', order: 0 }]);
                            }
                            setHasChanges(true);
                          }}
                          text="Add Student"
                          customClass="modern-button modern-add-button"
                        />
                      </div>
                    </>
                  ) : isEdited || isDetailEditing ? (
                    <div className="mt-3 text-center">
                      {visibleRows.map((row, ri) => (
                        <div
                          key={ri}
                          style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '5px' }}
                        >
                          {row.map(({ student }) => (
                            <div
                              key={student._id || student.name}
                              style={{
                                padding: '4px',
                                fontFamily: fontMapping[values.fontName],
                                fontSize: '0.8rem',
                              }}
                            >
                              {student.name} {student.surname}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {!isNameEditing && (isEdited || isDetailEditing) && !isReadOnly && (
                    <div className="mt-2 text-center">
                      <Button
                        onClick={() => setIsNameEditing(true)}
                        text="Edit Names"
                        customClass="modern-button modern-edit-button"
                      />
                    </div>
                  )}
                </div>
                {(isDetailEditing || isEdited) && (
                  <div className="col-12 col-md-4">
                    {isDetailEditing || isEdited ? (
                      <>
                        <h3>Edit Details</h3>
                        <div className="form-group">
                          <label>
                            <strong>Group Name</strong>
                          </label>
                          <Field name="groupName" className="form-control" disabled={isReadOnly} />
                          <ErrorMessage name="groupName" component="div" className="alert alert-danger" />
                        </div>
                        <div className="form-group">
                          <label>
                            <strong>Font</strong>
                          </label>
                          <Field
                            as="select"
                            name="fontName"
                            className="form-control"
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFontChange(e, setFieldValue)}
                            disabled={isReadOnly}
                          >
                            {Object.keys(fontMapping).map(key => (
                              <option key={key} value={key} style={{ fontFamily: fontMapping[key] }}>
                                {fontMapping[key]}
                              </option>
                            ))}
                          </Field>
                        </div>
                        <div className="d-flex" style={{ gap: '20px' }}>
                          {['crestLeft', 'crestRight'].map(field => (
                            <figure key={field} style={{ textAlign: 'center' }}>
                              <figcaption>
                                <strong>{field === 'crestLeft' ? 'Left Crest' : 'Right Crest'}</strong>
                              </figcaption>
                              <div className="img-thumbnail" style={crestContainerStyle}>
                                {values[field] ? (
                                  <img src={values[field]} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                ) : (
                                  <span>No Image</span>
                                )}
                              </div>

                              {!isReadOnly && (
                                <>
                                  <div className="mt-1 text-truncate" style={{ maxWidth: '150px' }}>
                                    {field === 'crestLeft' ? leftFileName : rightFileName}
                                  </div>
                                  <input
                                    id={field}
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    hidden
                                    onChange={e => handleFileChange(e, field, setFieldValue)}
                                  />
                                  <label htmlFor={field} className="btn btn-secondary mt-2">
                                    Choose file
                                  </label>
                                </>
                              )}
                            </figure>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <h3>Photo Details</h3>
                        <p>
                          <strong>Group:</strong> {currentImage.groupName}
                        </p>
                        <p>
                          <strong>Font:</strong> {fontMapping[values.fontName]}
                        </p>
                        <div className="d-flex" style={{ gap: '20px' }}>
                          {['crestLeft', 'crestRight'].map(c => (
                            <div key={c} style={crestContainerStyle}>
                              {currentImage[c]?.pic ? (
                                <img src={currentImage[c].pic} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                              ) : (
                                <span>No Image</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <hr className="my-4" />
              <div className="form-group">
                <label>
                  <p className="text-info">
                    <em>
                      <strong>School comment:</strong>{' '}
                      {isReadOnly
                        ? 'Here are the comments you attached to this photo.'
                        : 'Here you can add your comments / additional requests to this image. Feel free to provide any details or notes related to this photo.'}
                    </em>
                  </p>
                </label>
                <HTMLEditorQuill
                  onEmailBodyChange={c => setFieldValue('commentContent', c)}
                  value={values.commentContent}
                  isEnabled={!isReadOnly}
                />
                <ErrorMessage name="commentContent" component="div" className="alert alert-danger" />
              </div>
              {currentImage.comment?.content?.trim() && (
                <div style={commentMetaStyle}>
                  <div>
                    <strong>Created by:</strong>{' '}
                    {commentAuthor
                      ? `${commentAuthor.firstName} ${commentAuthor.lastName} (${commentAuthor.email})`
                      : 'Loading…'}{' '}
                    on {new Date(currentImage.comment.createdAt).toLocaleString()}
                  </div>
                  {currentImage.comment.updatedBy && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <strong>Last updated by:</strong>{' '}
                      {commentUpdater
                        ? `${commentUpdater.firstName} ${commentUpdater.lastName} (${commentUpdater.email})`
                        : 'Loading…'}{' '}
                      on {new Date(currentImage.comment.updatedAt!).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
              {!isReadOnly && (dirty || hasChanges) && (
                <div className="mt-3 text-right">
                  <button type="submit" className="modern-button modern-save-button">
                    Save
                  </button>
                  <Button
                    onClick={() => {
                      resetForm();
                      setHasChanges(false);
                      setIsDetailEditing(false);
                      setIsNameEditing(false);
                    }}
                    text="Cancel"
                    customClass="modern-button modern-cancel-button ml-2"
                  />
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
